import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './FAQ.css';

// All sections the FAQ page knows about. The `returns` block is
// gated below on the operator-controlled `returnsEnabled` setting;
// while disabled the section drops out entirely (and the lead copy
// likewise omits the "returns" word) so customers never see a policy
// the store hasn't formalised.
const ALL_SECTIONS = [
  { key: 'ordering', items: ['q1', 'q2', 'q3'] },
  { key: 'shipping', items: ['q1', 'q2', 'q3'] },
  { key: 'returns', items: ['q1', 'q2', 'q3'] },
  { key: 'payment', items: ['q1', 'q2', 'q3'] },
  { key: 'products', items: ['q1', 'q2', 'q3'] },
];

const FaqItem = ({ sectionKey, itemKey, isOpen, onToggle, t }) => {
  const id = `faq-${sectionKey}-${itemKey}`;
  return (
    <div className={`faq-item ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className="faq-item__trigger"
        aria-expanded={isOpen}
        aria-controls={`${id}-panel`}
        id={`${id}-button`}
        onClick={() => onToggle(id)}
      >
        <span className="faq-item__question">
          {t(`faq.${sectionKey}.${itemKey}.q`)}
        </span>
        <span className="faq-item__icon" aria-hidden="true">
          <Icon.ChevronDown
            size={ICON_DEFAULTS.size}
            strokeWidth={ICON_DEFAULTS.strokeWidth}
          />
        </span>
      </button>
      <div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-button`}
        className="faq-item__panel"
        hidden={!isOpen}
      >
        <p>{t(`faq.${sectionKey}.${itemKey}.a`)}</p>
      </div>
    </div>
  );
};

const FAQ = () => {
  const { t } = useTranslation();
  const { returnsEnabled } = useSiteSettings();
  const [openId, setOpenId] = useState(null);

  const handleToggle = useCallback((id) => {
    setOpenId((prev) => (prev === id ? null : id));
  }, []);

  // Drop the "returns" section while the operator hasn't enabled
  // the policy. The lead copy uses a separate translation variant
  // so the "returns" word doesn't appear there either.
  const sections = useMemo(
    () => ALL_SECTIONS.filter((s) => s.key !== 'returns' || returnsEnabled),
    [returnsEnabled]
  );

  const leadKey = returnsEnabled ? 'faq.lead' : 'faq.leadNoReturns';

  // Flatten the section/item structure into the [{ question, answer }]
  // shape FAQPage schema wants. This is what Google ingests to render
  // expandable Q/A rich results in the SERP.
  const faqEntries = useMemo(
    () =>
      sections.flatMap((s) =>
        s.items.map((itemKey) => ({
          question: t(`faq.${s.key}.${itemKey}.q`),
          answer: t(`faq.${s.key}.${itemKey}.a`),
        }))
      ),
    [sections, t]
  );

  return (
    <div className="faq-page">
      <SEO
        title={t('faq.title')}
        description={t(leadKey)}
        faq={faqEntries}
        breadcrumbs={[
          { name: t('nav.home', 'Home'), url: '/' },
          { name: t('faq.title'), url: '/faq' },
        ]}
      />

      <header className="faq-hero">
        <span className="faq-eyebrow">{t('faq.eyebrow')}</span>
        <h1 className="faq-title">{t('faq.title')}</h1>
        <p className="faq-lead">{t(leadKey)}</p>
      </header>

      <div className="faq-content">
        {sections.map((section) => (
          <section key={section.key} className="faq-section">
            <h2 className="faq-section__title">{t(`faq.${section.key}.title`)}</h2>
            <div className="faq-section__items">
              {section.items.map((itemKey) => {
                const id = `faq-${section.key}-${itemKey}`;
                return (
                  <FaqItem
                    key={id}
                    sectionKey={section.key}
                    itemKey={itemKey}
                    isOpen={openId === id}
                    onToggle={handleToggle}
                    t={t}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default FAQ;
